'use client';

import { logger } from '@adornis/base/logging.js';
import { ArrowLeft, School, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getClassRoomByCodeQuery } from '../../../db/api/get-classroom-by-code.js';
import { getClassRoomsByIdsQuery } from '../../../db/api/get-classrooms-by-ids.js';
import { getMyClassMembershipsQuery } from '../../../db/api/get-my-class-memberships.js';
import { requestToJoinClassMutation } from '../../../db/api/request-to-join-class.js';
import type { ClassMembership, ClassMembershipStatus } from '../../../db/class-membership.js';
import type { ClassRoom } from '../../../db/classroom.js';
import { isSubject } from '../../../db/subjects.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';
import { StudentClassPage } from './student-class-page.js';

interface JoinClassPageProps {
  onBack: () => void;
  initialCode?: string | null;
  /** Called once after the initial-code lookup has been triggered, so the caller can clear its
   * stashed value and avoid re-triggering the lookup on a later, unrelated remount of this page. */
  onInitialCodeConsumed?: () => void;
}

const STATUS_STYLES: Record<ClassMembershipStatus, string> = {
  pending: 'bg-amber-500',
  approved: 'bg-green-600',
  rejected: 'bg-destructive',
};

export function JoinClassPage({ onBack, initialCode, onInitialCodeConsumed }: JoinClassPageProps) {
  const { t } = useTranslation();

  const [codeInput, setCodeInput] = useState(initialCode ?? '');
  const [lookupResult, setLookupResult] = useState<ClassRoom | null>(null);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const [memberships, setMemberships] = useState<ClassMembership[]>([]);
  const [classroomsByID, setClassroomsByID] = useState<Record<string, ClassRoom>>({});
  const [isLoadingMemberships, setIsLoadingMemberships] = useState(false);
  const [selectedClassRoomID, setSelectedClassRoomID] = useState<string | null>(null);

  const loadMemberships = () => {
    setIsLoadingMemberships(true);
    void getMyClassMembershipsQuery()({ _id: 1, classRoomID: 1, status: 1, requestedAt: 1 })
      .then(rows => {
        setMemberships(rows ?? []);
        const ids = Array.from(new Set((rows ?? []).map(m => m.classRoomID)));
        if (ids.length === 0) return;
        return getClassRoomsByIdsQuery(ids)({ _id: 1, name: 1, subject: 1 }).then(classrooms => {
          const map: Record<string, ClassRoom> = {};
          for (const classroom of classrooms ?? []) map[classroom._id] = classroom;
          setClassroomsByID(map);
        });
      })
      .catch(err => logger.error(err, 'Failed to load class memberships'))
      .finally(() => setIsLoadingMemberships(false));
  };

  useEffect(() => {
    loadMemberships();
  }, []);

  const handleLookup = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setIsLookingUp(true);
    setJoinError(null);
    try {
      const results = await getClassRoomByCodeQuery(trimmed)({ _id: 1, name: 1 });
      setLookupResult(results?.[0] ?? null);
      setLookupAttempted(true);
    } catch (err) {
      logger.error(err, 'Failed to look up class by code');
      setLookupResult(null);
      setLookupAttempted(true);
    } finally {
      setIsLookingUp(false);
    }
  };

  useEffect(() => {
    if (initialCode) void handleLookup(initialCode);
    onInitialCodeConsumed?.();
    // Only run once on mount for the deep-link/pending-code handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = async () => {
    if (!lookupResult) return;

    setIsJoining(true);
    setJoinError(null);
    try {
      await requestToJoinClassMutation(codeInput.trim())({ _id: 1, status: 1 });
      setLookupResult(null);
      setLookupAttempted(false);
      setCodeInput('');
      loadMemberships();
    } catch (err) {
      logger.error(err, 'Failed to request to join class');
      setJoinError(t('JoinClass.alerts.joinError'));
    } finally {
      setIsJoining(false);
    }
  };

  const existingMembership = lookupResult ? memberships.find(m => m.classRoomID === lookupResult._id) : null;

  // Master/detail in local state, exactly as the teacher's ClassroomPage swaps its grid for ClassRoster —
  // no route of its own, so the student lands back on the grid with its state intact.
  const selectedClassroom = selectedClassRoomID ? classroomsByID[selectedClassRoomID] : null;
  if (selectedClassroom) {
    return <StudentClassPage classroom={selectedClassroom} onBack={() => setSelectedClassRoomID(null)} />;
  }

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground tracking-tight">{t('JoinClass.title')}</h1>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('JoinClass.enterCodeTitle')}</h2>
            <div className="flex items-center gap-2">
              <Input
                value={codeInput}
                onChange={e => {
                  setCodeInput(e.target.value.toUpperCase());
                  setLookupResult(null);
                  setLookupAttempted(false);
                }}
                placeholder={t('JoinClass.codePlaceholder')}
                className="font-mono tracking-widest max-w-[220px]"
                maxLength={6}
                onKeyDown={e => {
                  if (e.key === 'Enter') void handleLookup(codeInput);
                }}
              />
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => void handleLookup(codeInput)}
                disabled={isLookingUp || !codeInput.trim()}
              >
                <Search className="h-4 w-4" /> {t('JoinClass.findButton')}
              </Button>
            </div>

            {lookupAttempted && !isLookingUp && (
              <div className="mt-4">
                {!lookupResult ? (
                  <p className="text-sm text-destructive">{t('JoinClass.codeNotFound')}</p>
                ) : existingMembership ? (
                  <div className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg">
                    <p className="text-sm text-foreground">
                      {t('JoinClass.alreadyRequested', { className: lookupResult.name })}
                    </p>
                    <Badge className={STATUS_STYLES[existingMembership.status]}>
                      {t(`JoinClass.status.${existingMembership.status}`)}
                    </Badge>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 p-4 bg-background border border-border rounded-lg">
                    <p className="text-sm text-foreground">
                      {t('JoinClass.confirmJoin', { className: lookupResult.name })}
                    </p>
                    <Button size="sm" className="gap-1.5" onClick={() => void handleJoin()} disabled={isJoining}>
                      <UserPlus className="h-3.5 w-3.5" /> {t('JoinClass.requestButton')}
                    </Button>
                  </div>
                )}
                {joinError && <p className="text-sm text-destructive mt-2">{joinError}</p>}
              </div>
            )}
          </section>

          {/* No card chrome around this one, unlike the code-entry section above it: the tiles are
              themselves `bg-card`, and nesting them in a card would flatten them into the background the
              way the teacher grid never is. */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-6">{t('JoinClass.myClassesTitle')}</h2>

            {isLoadingMemberships ? (
              <p className="text-sm text-muted-foreground">{t('JoinClass.loading')}</p>
            ) : memberships.length === 0 ? (
              <p className="text-sm italic text-muted-foreground/70">{t('JoinClass.noClasses')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {memberships.map(membership => {
                  const classroom = classroomsByID[membership.classRoomID];
                  // Only an approved membership has anything behind it — a pending or rejected class has no
                  // results to show, so its tile stays inert rather than opening an empty detail view.
                  const isOpenable = membership.status === 'approved' && Boolean(classroom);

                  // The SUBJECT is the tile's headline, not the class name: a student already knows which
                  // grade they are in, so "Sozialkunde" tells them more at a glance than "LK 12". The class
                  // name keeps the pill slot beside the status badge. Classrooms predating `subject` fall
                  // back to the name as the headline so a tile is never left untitled.
                  const subjectLabel = classroom?.subject
                    ? isSubject(classroom.subject)
                      ? t(`Subjects.${classroom.subject}`)
                      : classroom.subject
                    : null;
                  const className = classroom?.name ?? membership.classRoomID;

                  const tileContent = (
                    <>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-9 h-9 rounded-lg bg-brand-surface flex items-center justify-center shrink-0">
                          <School className="h-4 w-4 text-brand-text" />
                        </div>
                        <p className="font-semibold text-foreground truncate min-w-0">{subjectLabel ?? className}</p>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {subjectLabel && (
                          <span className="px-2 py-0.5 bg-brand-surface text-brand-text border border-brand-border rounded text-xs font-medium">
                            {className}
                          </span>
                        )}
                        <Badge className={STATUS_STYLES[membership.status]}>
                          {t(`JoinClass.status.${membership.status}`)}
                        </Badge>
                      </div>
                    </>
                  );

                  return (
                    <div
                      key={membership._id}
                      className="relative flex flex-col bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                    >
                      {isOpenable ? (
                        <button
                          type="button"
                          onClick={() => setSelectedClassRoomID(membership.classRoomID)}
                          className="text-left p-4 sm:p-5 flex-1"
                        >
                          {tileContent}
                        </button>
                      ) : (
                        <div className="p-4 sm:p-5 flex-1">{tileContent}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
